// packages/web/src/components/CommunicationsPage.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { db, functions } from '../firebase';
import { collection, query, orderBy, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faRobot, faPlus, faPaperPlane, faCalendarAlt, faEdit, faHistory, faBirthdayCake, faHeart, faUserPlus, faBell, faEnvelope, faChevronLeft, faChevronRight, faChevronDown, faChevronUp, faTimes, faInfoCircle, faSyncAlt, faHandPeace, faCommentDots, faNewspaper } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { Alert, Tab, Tabs, Badge } from 'react-bootstrap';
import { format } from 'date-fns';
import NewsletterTab from './NewsletterTab';
import './CommunicationsPage.css';

const PAGE_SIZE = 10;

// Trigger descriptions for display
const TRIGGER_INFO = {
  birthday: { label: 'Birthday', description: 'Daily at 9am for members with birthdays', icon: faBirthdayCake },
  wedding_anniversary: { label: 'Anniversary', description: 'Daily at 9am for wedding anniversaries', icon: faHeart },
  first_login: { label: 'Welcome', description: 'When member creates an account', icon: faUserPlus },
  member_invite: { label: 'Invite', description: 'When a member with an email is added or their email is updated', icon: faEnvelope },
  directory_update: { label: 'Directory Update', description: 'Send on demand to request info updates', icon: faSyncAlt },
  re_engagement: { label: 'Re-Engage', description: 'Send on demand to inactive members', icon: faHandPeace },
  event_feedback: { label: 'Feedback', description: 'Send on demand to request event feedback', icon: faCommentDots }
};

const getTriggerLabel = (event) => TRIGGER_INFO[event]?.label || event;
const getTriggerDescription = (event) => TRIGGER_INFO[event]?.description || '';
const getTriggerIcon = (event) => TRIGGER_INFO[event]?.icon || faRobot;

// Replace template variables with friendly placeholders for display
const formatTemplateText = (text) => {
  if (!text) return '';
  return text
    .replace(/\{\{member\.name\}\}/g, '[Member]')
    .replace(/\{\{member\.surname\}\}/g, '[Surname]')
    .replace(/\{\{org\.name\}\}/g, '[Church Name]')
    .replace(/\{\{family\.surname\}\}/g, '[Family Name]')
    .replace(/\{\{[^}]+\}\}/g, '[...]'); // Catch any other variables
};

function CommunicationsPage() {
  const { currentUser, userOrgId, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'announcements');
  const [announcements, setAnnouncements] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [automationsLoading, setAutomationsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [announcementsPage, setAnnouncementsPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  // Expanded history items
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showBanner, setShowBanner] = useState(() => !localStorage.getItem('dismissCommsBanner'));
  const [sendingAutomation, setSendingAutomation] = useState(null); // automation being confirmed
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!currentUser || !isAdmin || !userOrgId) return;
    fetchData();
  }, [currentUser, isAdmin, userOrgId]);

  // Reset pagination when tab changes
  useEffect(() => {
    setAnnouncementsPage(1);
    setHistoryPage(1);
  }, [activeTab]);

  // Ensure automations exist when automations tab is selected
  useEffect(() => {
    if (activeTab === 'automations' && currentUser && isAdmin && userOrgId) {
      ensureAutomations();
    }
  }, [activeTab, currentUser, isAdmin, userOrgId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all communications for this org
      const communicationsQuery = query(
        collection(db, 'communications'),
        where('orgId', '==', userOrgId),
        orderBy('createdAt', 'desc')
      );
      const communicationsSnap = await getDocs(communicationsQuery);
      const communicationsData = communicationsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Split into drafts (for Announcements tab) and sent history
      const drafts = communicationsData.filter(c => c.status === 'draft' || c.status === 'scheduled');
      const sentHistory = communicationsData.filter(c => c.status === 'sent' || c.status === 'sending' || c.status === 'failed');

      setAnnouncements(drafts);
      setHistory(sentHistory);
    } catch (err) {
      console.error('Error fetching communications data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const ensureAutomations = async () => {
    setAutomationsLoading(true);
    try {
      const ensureOrgAutomationsFn = httpsCallable(functions, 'ensureOrgAutomations');
      const result = await ensureOrgAutomationsFn();
      setAutomations(result.data.automations || []);
    } catch (err) {
      console.error('Error ensuring automations:', err);
      setError(err.message);
    } finally {
      setAutomationsLoading(false);
    }
  };

  const handleToggleAutomation = async (automation) => {
    try {
      const automationRef = doc(db, 'automations', automation.id);
      await updateDoc(automationRef, { enabled: !automation.enabled });
      setAutomations(prev => prev.map(a =>
        a.id === automation.id ? { ...a, enabled: !a.enabled } : a
      ));
    } catch (err) {
      console.error('Error toggling automation:', err);
      setError(err.message);
    }
  };

  const getTargetingSummary = (automation) => {
    const targeting = automation.targeting || { type: 'all' };
    if (targeting.type === 'groups' && targeting.groupIds?.length) {
      return `${targeting.groupIds.length} group(s)`;
    }
    if (targeting.type === 'families' && targeting.familyIds?.length) {
      return `${targeting.familyIds.length} family/families`;
    }
    return 'All Members';
  };

  const handleSendManualAutomation = async () => {
    if (!sendingAutomation) return;
    setIsSending(true);
    try {
      const sendFn = httpsCallable(functions, 'sendManualAutomation');
      const result = await sendFn({ automationId: sendingAutomation.id });
      const stats = result.data.stats;
      setSendingAutomation(null);
      // Refresh automations to update lastTriggeredAt
      ensureAutomations();
      // Refresh history
      fetchData();
      setError(null);
      alert(`Sent successfully! ${stats.delivered} delivered out of ${stats.targeted} targeted.`);
    } catch (err) {
      console.error('Error sending manual automation:', err);
      setError(err.message || 'Failed to send automation.');
    } finally {
      setIsSending(false);
      setSendingAutomation(null);
    }
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'secondary',
      scheduled: 'info',
      sending: 'warning',
      sent: 'success',
      failed: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  // Pagination helpers
  const paginate = (items, page) => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  };

  const getTotalPages = (items) => Math.ceil(items.length / PAGE_SIZE);

  const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
      <div className="communications-pagination">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    );
  };

  if (!isAdmin) {
    return (
      <div className="communications-page">
        <div className="container mt-3">
          <Alert variant="warning">
            You do not have permission to access this page.
          </Alert>
        </div>
      </div>
    );
  }

  const paginatedAnnouncements = paginate(announcements, announcementsPage);
  const paginatedHistory = paginate(history, historyPage);

  return (
    <div className="communications-page">
      <div className="communications-header">
        <div className="container">
          <div className="communications-header-content">
            <h1 className="communications-title">Communications <span className="admin-only-badge">Admin Only</span></h1>
            {activeTab === 'announcements' && (
              <Link to="/communications/new" className="btn btn-primary">
                <FontAwesomeIcon icon={faPlus} /> New Announcement
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container">
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {showBanner && (
          <div className="comms-guide-banner">
            <div className="comms-guide-content">
              <FontAwesomeIcon icon={faInfoCircle} className="comms-guide-icon" />
              <div className="comms-guide-text">
Use <strong>Announcements</strong> to send messages to your organization, <strong>Automations</strong> to set up birthday, anniversary, and welcome notifications, and <strong>History</strong> to see everything that's been sent.
              </div>
            </div>
            <button
              className="comms-guide-dismiss"
              onClick={() => { localStorage.setItem('dismissCommsBanner', 'true'); setShowBanner(false); }}
              title="Dismiss"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="communications-tabs">
          <Tab eventKey="announcements" title={<><FontAwesomeIcon icon={faBullhorn} /> Announcements</>}>
            {loading ? (
              <div className="communications-loading">Loading...</div>
            ) : announcements.length === 0 ? (
              <div className="communications-empty">
                <FontAwesomeIcon icon={faBullhorn} size="3x" />
                <h4>No announcements yet</h4>
                <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                  Announcements are sent to everyone in your organization, or to specific groups.
                </p>
                <Link to="/communications/new" className="btn btn-outline-primary">
                  Create Your First Announcement
                </Link>
              </div>
            ) : (
              <>
                <div className="announcements-list">
                  {paginatedAnnouncements.map(announcement => (
                    <div key={announcement.id} className="announcement-card">
                      <div className="announcement-info">
                        <h3>{announcement.title}</h3>
                        <p className="announcement-preview">{announcement.body.substring(0, 100)}...</p>
                        <div className="announcement-meta">
                          {getStatusBadge(announcement.status)}
                          <span className="meta-item">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            {format(announcement.createdAt?.toDate?.() || new Date(announcement.createdAt), 'MMM d, yyyy')}
                          </span>
                          {announcement.stats?.delivered > 0 && (
                            <span className="meta-item">
                              <FontAwesomeIcon icon={faPaperPlane} />
                              {announcement.stats.delivered} delivered
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="announcement-actions">
                        {announcement.status === 'draft' && (
                          <Link to={`/communications/${announcement.id}/edit`} className="btn btn-sm btn-outline-secondary">
                            <FontAwesomeIcon icon={faEdit} />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <PaginationControls
                  currentPage={announcementsPage}
                  totalPages={getTotalPages(announcements)}
                  onPageChange={setAnnouncementsPage}
                />
              </>
            )}
          </Tab>

          <Tab eventKey="automations" title={<><FontAwesomeIcon icon={faRobot} /> Automations</>}>
            {automationsLoading ? (
              <div className="communications-loading">Loading...</div>
            ) : (
              <div className="automations-list">
                {/* Automatic Automations Section */}
                {automations.filter(a => a.trigger?.type !== 'manual').length > 0 && (
                  <>
                    <div className="automations-section-header">
                      <FontAwesomeIcon icon={faRobot} />
                      <span>Automatic</span>
                    </div>
                    {automations.filter(a => a.trigger?.type !== 'manual').map(automation => (
                      <div
                        key={automation.id}
                        className={`automation-card ${!automation.enabled ? 'automation-disabled' : ''}`}
                        data-trigger={automation.trigger?.event}
                      >
                        <div className="automation-accent"></div>
                        <div className="automation-content">
                          <div className="automation-trigger-icon">
                            <FontAwesomeIcon icon={getTriggerIcon(automation.trigger?.event)} />
                          </div>
                          <div className="automation-info">
                            <h3>{automation.name}</h3>
                            <div className="automation-trigger">
                              <Badge bg="primary">
                                {getTriggerLabel(automation.trigger?.event)}
                              </Badge>
                              <span className="trigger-description">
                                {getTriggerDescription(automation.trigger?.event)}
                              </span>
                            </div>
                            <div className="automation-channels">
                              {automation.channels?.push && (
                                <Badge bg="info">
                                  <FontAwesomeIcon icon={faBell} /> Push
                                </Badge>
                              )}
                              {automation.channels?.email && (
                                <Badge bg="info">
                                  <FontAwesomeIcon icon={faEnvelope} /> Email
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="automation-controls">
                            <div className="automation-toggle">
                              <label className="toggle-label">{automation.enabled ? 'Tap to disable' : 'Tap to enable'}</label>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={automation.enabled}
                                  onChange={() => handleToggleAutomation(automation)}
                                />
                              </div>
                            </div>
                            <div className="automation-actions">
                              <Link to={`/communications/automations/${automation.id}/edit`} className="btn btn-sm btn-outline-secondary">
                                <FontAwesomeIcon icon={faEdit} />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Manual Automations Section */}
                {automations.filter(a => a.trigger?.type === 'manual').length > 0 && (
                  <>
                    <div className="automations-section-header">
                      <FontAwesomeIcon icon={faPaperPlane} />
                      <span>Manual</span>
                    </div>
                    <p className="automations-section-description">
                      Reusable message templates you can send on demand. Edit the message and targeting, then hit Send Now when you're ready.
                    </p>
                    {automations.filter(a => a.trigger?.type === 'manual').map(automation => (
                      <div
                        key={automation.id}
                        className="automation-card"
                        data-trigger={automation.trigger?.event}
                      >
                        <div className="automation-accent"></div>
                        <div className="automation-content">
                          <div className="automation-trigger-icon">
                            <FontAwesomeIcon icon={getTriggerIcon(automation.trigger?.event)} />
                          </div>
                          <div className="automation-info">
                            <h3>{automation.name}</h3>
                            <div className="automation-trigger">
                              <Badge bg="secondary">
                                {getTriggerLabel(automation.trigger?.event)}
                              </Badge>
                              <span className="trigger-description">
                                {getTriggerDescription(automation.trigger?.event)}
                              </span>
                            </div>
                            <div className="automation-channels">
                              {automation.channels?.push && (
                                <Badge bg="info">
                                  <FontAwesomeIcon icon={faBell} /> Push
                                </Badge>
                              )}
                              {automation.channels?.email && (
                                <Badge bg="info">
                                  <FontAwesomeIcon icon={faEnvelope} /> Email
                                </Badge>
                              )}
                              {automation.lastTriggeredAt && (
                                <span className="automation-last-sent">
                                  Last sent: {format(automation.lastTriggeredAt?.toDate?.() || new Date(automation.lastTriggeredAt._seconds * 1000), 'MMM d, yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="automation-controls">
                            <button
                              className="btn btn-sm btn-primary automation-send-btn"
                              onClick={() => setSendingAutomation(automation)}
                            >
                              <FontAwesomeIcon icon={faPaperPlane} /> Send Now
                            </button>
                            <div className="automation-actions">
                              <Link to={`/communications/automations/${automation.id}/edit`} className="btn btn-sm btn-outline-secondary">
                                <FontAwesomeIcon icon={faEdit} />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Send Confirmation Modal */}
            {sendingAutomation && (
              <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '0.5rem' }} />
                        Send Automation?
                      </h5>
                      <button type="button" className="btn-close" onClick={() => setSendingAutomation(null)} disabled={isSending}></button>
                    </div>
                    <div className="modal-body">
                      <p>You are about to send:</p>
                      <p><strong>{sendingAutomation.name}</strong></p>
                      <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.25rem' }} />
                        Target: {getTargetingSummary(sendingAutomation)}
                      </p>
                      <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        Messages will be delivered via {[
                          sendingAutomation.channels?.push && 'push notification',
                          sendingAutomation.channels?.email && 'email'
                        ].filter(Boolean).join(' and ')}.
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setSendingAutomation(null)} disabled={isSending}>Cancel</button>
                      <button type="button" className="btn btn-primary" onClick={handleSendManualAutomation} disabled={isSending}>
                        {isSending ? 'Sending...' : <><FontAwesomeIcon icon={faPaperPlane} /> Send Now</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Tab>

          <Tab eventKey="newsletter" title={<><FontAwesomeIcon icon={faNewspaper} /> Newsletter</>}>
            <NewsletterTab />
          </Tab>

          <Tab eventKey="history" title={<><FontAwesomeIcon icon={faHistory} /> History</>}>
            {loading ? (
              <div className="communications-loading">Loading...</div>
            ) : history.length === 0 ? (
              <div className="communications-empty">
                <FontAwesomeIcon icon={faHistory} size="3x" />
                <h4>No communications sent yet</h4>
                <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                  Sent announcements and triggered automations will appear here.
                </p>
              </div>
            ) : (
              <>
                <div className="history-list">
                  {paginatedHistory.map(item => {
                    const isExpanded = expandedItems.has(item.id);
                    const bodyText = item.renderedBody || formatTemplateText(item.body) || '';
                    const isLongText = bodyText.length > 120;

                    return (
                      <div
                        key={item.id}
                        className={`history-card ${isExpanded ? 'history-card-expanded' : ''}`}
                        data-type={item.type || 'announcement'}
                      >
                        <div className="history-type-icon">
                          <FontAwesomeIcon
                            icon={item.type === 'automation' ? faRobot : faBullhorn}
                            title={item.type === 'automation' ? 'Automation' : 'Announcement'}
                          />
                        </div>
                        <div className="history-info">
                          <div className="history-header">
                            <h3>{item.renderedTitle || formatTemplateText(item.title)}</h3>
                            <Badge bg={item.type === 'automation' ? 'info' : 'primary'}>
                              {item.type === 'automation' ? 'Automation' : 'Announcement'}
                            </Badge>
                          </div>
                          <div className={`history-body ${isExpanded ? 'history-body-expanded' : ''}`}>
                            {isExpanded ? bodyText : `${bodyText.substring(0, 120)}${isLongText ? '...' : ''}`}
                          </div>
                          {isLongText && (
                            <button
                              className="history-expand-btn"
                              onClick={() => toggleExpanded(item.id)}
                            >
                              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          )}
                          {item.recipients && item.recipients.length > 0 && (
                            <div className="history-recipients">
                              <span className="recipients-label">Sent to:</span>
                              <span className="recipients-list">
                                {item.recipients.slice(0, 3).map(r => r.name).join(', ')}
                                {item.recipients.length > 3 && ` +${item.recipients.length - 3} more`}
                              </span>
                            </div>
                          )}
                          <div className="history-meta">
                            {getStatusBadge(item.status)}
                            {item.channels?.push && (
                              <Badge bg="info">
                                <FontAwesomeIcon icon={faBell} /> Push
                              </Badge>
                            )}
                            {item.channels?.email && (
                              <Badge bg="info">
                                <FontAwesomeIcon icon={faEnvelope} /> Email
                              </Badge>
                            )}
                            <span className="meta-item">
                              <FontAwesomeIcon icon={faCalendarAlt} />
                              {format(item.sentAt?.toDate?.() || item.createdAt?.toDate?.() || new Date(), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          {item.stats && (
                            <div className="history-stats">
                              <span className="stat-item stat-targeted">
                                <strong>{item.stats.targeted || 0}</strong> targeted
                              </span>
                              <span className="stat-item stat-delivered">
                                <strong>{item.stats.delivered || 0}</strong> delivered
                              </span>
                              {(item.stats.failed > 0 || item.stats.unreachable > 0) && (
                                <span className="stat-item stat-failed">
                                  <strong>{(item.stats.failed || 0) + (item.stats.unreachable || 0)}</strong> failed/unreachable
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <PaginationControls
                  currentPage={historyPage}
                  totalPages={getTotalPages(history)}
                  onPageChange={setHistoryPage}
                />
              </>
            )}
          </Tab>
        </Tabs>
      </div>

    </div>
  );
}

export default CommunicationsPage;
